import { Component, EventEmitter, Input, Output, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-checkbox',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
  ],
})
export class CheckboxComponent implements ControlValueAccessor {
  @Input() checked: boolean = false;
  @Input() indeterminate: boolean = false;
  @Input() multiSelected: boolean = false;
  @Input() label: string = '';
  @Input() disabled: boolean = false;
  @Input() error: boolean = false;
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() className: string = '';

  @Output() checkedChange = new EventEmitter<boolean>();
  @Output() change = new EventEmitter<boolean>();

  private onChange: (value: boolean) => void = () => {};
  private onTouched: () => void = () => {};

  get isMultiSelected(): boolean {
    return this.indeterminate || this.multiSelected;
  }

  toggle(event: Event): void {
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    this.checked = !this.checked;
    this.indeterminate = false;
    this.multiSelected = false;
    this.onChange(this.checked);
    this.onTouched();
    this.checkedChange.emit(this.checked);
    this.change.emit(this.checked);
  }

  // ControlValueAccessor implementation
  writeValue(value: boolean): void {
    this.checked = !!value;
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get boxStateClasses(): string {
    if (this.disabled) {
      return 'checkbox-disabled border-checkbox-border-disabled bg-transparent cursor-not-allowed opacity-50';
    }

    if (this.isMultiSelected || this.checked) {
      return 'checkbox-filled bg-checkbox-bg-filled border-transparent';
    }

    if (this.error) {
      return 'checkbox-error border-checkbox-border-error bg-transparent hover:bg-checkbox-bg-hover';
    }

    return 'checkbox-default border-checkbox-border-default bg-transparent hover:bg-checkbox-bg-hover';
  }
}
